import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import homecrowdIcon from '../assets/images/icon-transparent.png';
import apiService from '../services/api';
import {
  defaultEmbeddingDocsDocument,
  normalizeEmbeddingDocsDocument,
} from '../data/embeddingDocsDefaults';
import '../styles/EmbeddingDocs.css';

function CodeCard({ label, code }) {
  return (
    <div className="embedding-docs-code-card">
      <div className="embedding-docs-code-label">{label}</div>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}

function isValidDocument(document) {
  return Boolean(document && typeof document === 'object' && Array.isArray(document.topics) && document.topics.length);
}

function renderBlock(block, key) {
  if (block.type === 'paragraph') {
    return <p key={key}>{block.text}</p>;
  }

  if (block.type === 'code') {
    return <CodeCard key={key} label={block.label || 'Example'} code={block.code || ''} />;
  }

  if (block.type === 'callout') {
    return (
      <div key={key} className="embedding-docs-callout">
        {block.title ? <strong>{block.title}</strong> : null}
        <span>{block.text || ''}</span>
      </div>
    );
  }

  if (block.type === 'list') {
    return (
      <ul key={key} className="embedding-docs-list">
        {(block.items || []).map((item, index) => (
          <li key={`${key}-${index}`}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.type === 'downloadBlock') {
    const hasHref = typeof block.href === 'string' && block.href.trim() !== '' && block.href !== '#';
    return (
      <div key={key} className="embedding-docs-link-block embedding-docs-download-block">
        {block.title ? <div className="embedding-docs-link-block-title">{block.title}</div> : null}
        {hasHref ? (
          <a
            className="embedding-docs-download-button"
            href={block.href}
            {...(block.downloadName ? { download: block.downloadName } : {})}
            target="_self"
            rel="noopener"
          >
            {block.buttonLabel || 'Download'}
          </a>
        ) : (
          <button type="button" className="embedding-docs-download-button" disabled>
            {block.buttonLabel || 'Download'}
          </button>
        )}
        {block.helperText ? (
          <p className="embedding-docs-download-helper">{block.helperText}</p>
        ) : null}
      </div>
    );
  }

  if (block.type === 'linkList') {
    return (
      <div key={key} className="embedding-docs-link-block">
        {block.title ? <div className="embedding-docs-link-block-title">{block.title}</div> : null}
        <ul className="embedding-docs-list embedding-docs-link-block-list">
          {(block.items || []).map((item, index) => (
            <li key={`${key}-link-${index}`}>
              {item?.href ? (
                <>
                  <a
                    className="embedding-docs-external-link"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.label || item.href}
                  </a>
                  {item.caption ? (
                    <span className="embedding-docs-link-caption">{item.caption}</span>
                  ) : null}
                </>
              ) : (
                item?.label
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (block.type === 'table') {
    const columns = block.columns || [];
    const rows = block.rows || [];
    const gridTemplateColumns = `repeat(${Math.max(columns.length, 1)}, minmax(0, 1fr))`;
    return (
      <div key={key} className="embedding-docs-table">
        <div className="embedding-docs-table-row embedding-docs-table-head" style={{ gridTemplateColumns }}>
          {columns.map((column, index) => (
            <div key={`${key}-column-${index}`}>{column}</div>
          ))}
        </div>
        {rows.map((row, rowIndex) => (
          <div key={`${key}-row-${rowIndex}`} className="embedding-docs-table-row" style={{ gridTemplateColumns }}>
            {row.map((cell, cellIndex) => (
              <div key={`${key}-row-${rowIndex}-cell-${cellIndex}`}>{cell}</div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return null;
}

function TopicBody({ topic }) {
  return (
    <>
      {(topic.subtopics || []).map((subtopic) => (
        <section key={subtopic.id} id={subtopic.id} className="embedding-docs-subsection">
          <h3>{subtopic.label}</h3>
          {(subtopic.blocks || []).map((block, index) => renderBlock(block, `${subtopic.id}-${index}`))}
        </section>
      ))}
    </>
  );
}

function EmbeddingDocs() {
  const { topic: topicSlug } = useParams();
  const location = useLocation();
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    const storedTheme = window.localStorage.getItem('embedding-docs-theme');
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }

    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  });
  const [documentData, setDocumentData] = useState(() => normalizeEmbeddingDocsDocument(defaultEmbeddingDocsDocument));

  useEffect(() => {
    window.localStorage.setItem('embedding-docs-theme', theme);
  }, [theme]);

  useEffect(() => {
    let ignore = false;

    const loadDocument = async () => {
      try {
        const response = await apiService.getEmbeddingDocs();
        if (!ignore && isValidDocument(response?.document)) {
          setDocumentData(normalizeEmbeddingDocsDocument(response.document));
        }
      } catch (error) {
        console.error('Failed to load embedding docs:', error);
      }
    };

    loadDocument();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    const target = document.getElementById(location.hash.replace('#', ''));
    if (target) {
      window.setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }, [location.pathname, location.hash]);

  const topics = useMemo(() => {
    return (documentData.topics || []).map((topic, index) => ({
      ...topic,
      number: String(index + 1),
    }));
  }, [documentData]);

  const firstTopicSlug = topics[0]?.slug;
  const activeTopic = topics.find((item) => item.slug === topicSlug) || topics[0];

  if (!firstTopicSlug) {
    return null;
  }

  if (!topicSlug) {
    return <Navigate to={`/docs/embedding/${firstTopicSlug}/`} replace />;
  }

  if (!topics.some((item) => item.slug === topicSlug)) {
    return <Navigate to={`/docs/embedding/${firstTopicSlug}/`} replace />;
  }

  return (
    <div className={`embedding-docs-page embedding-docs-page--${theme}`}>
      <header className="embedding-docs-topbar">
        <div className="embedding-docs-topbar-inner">
          <Link className="embedding-docs-brand" to="/docs/embedding/overview/">
            <img className="embedding-docs-brand-icon" src={homecrowdIcon} alt="Homecrowd" />
            Homecrowd Docs
          </Link>
          <button
            type="button"
            className="embedding-docs-theme-toggle"
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
          >
            <span className="embedding-docs-theme-toggle-track">
              <span className="embedding-docs-theme-toggle-thumb" />
            </span>
            <span className="embedding-docs-theme-toggle-label">
              {theme === 'light' ? 'Light' : 'Dark'}
            </span>
          </button>
        </div>
      </header>

      <main className="embedding-docs-shell">
        <aside className="embedding-docs-sidebar">
          <div className="embedding-docs-sidebar-card">
            <div className="embedding-docs-sidebar-eyebrow">{documentData.sidebarEyebrow || 'Embedding'}</div>
            <h1 className="embedding-docs-sidebar-title">{documentData.sidebarTitle || 'Homecrowd WebView Guide'}</h1>
            <p className="embedding-docs-sidebar-copy">
              {documentData.sidebarCopy || ''}
            </p>

            <nav className="embedding-docs-nav" aria-label="Embedding documentation topics">
              {topics.map((topic) => (
                <Link
                  key={topic.slug}
                  className={`embedding-docs-nav-item ${activeTopic.slug === topic.slug ? 'active' : ''}`}
                  to={`/docs/embedding/${topic.slug}/`}
                >
                  <span className="embedding-docs-nav-number">{topic.number}</span>
                  <span className="embedding-docs-nav-label">{topic.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </aside>

        <article className="embedding-docs-content">
          <section className="embedding-docs-topic-hero">
            <div className="embedding-docs-section-number">{activeTopic.number}</div>
            <div className="embedding-docs-section-body">
              <p className="embedding-docs-kicker">{activeTopic.label}</p>
              <h2>{activeTopic.title}</h2>
              <p>{activeTopic.intro}</p>
            </div>
          </section>

          <nav className="embedding-docs-subnav" aria-label={`${activeTopic.label} subtopics`}>
            {activeTopic.subtopics.map((subtopic) => (
              <a key={subtopic.id} className="embedding-docs-subnav-item" href={`#${subtopic.id}`}>
                {subtopic.label}
              </a>
            ))}
          </nav>

          <div className="embedding-docs-topic-content">
            <TopicBody topic={activeTopic} />
          </div>
        </article>
      </main>
    </div>
  );
}

export default EmbeddingDocs;
